import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'

import Grid2 from '@mui/material/Unstable_Grid2'

import { useKeyMetricActions } from 'redux/actions/KeyMetrics'

import { SxProps } from '@mui/material'
import {
  getResourceVariant,
  isDashboardResource,
  TKeyMetric
} from 'types/GlobalKeyMetric'

import { useGroupIdsForKeyMetric } from 'redux/hooks/GroupKeyMetricLinker'
import { useGroup, useGroups } from 'redux/hooks/Groups'
import {
  useKeyMetric,
  useKeyMetricRawData,
  useKeyMetricRawDataFetching
} from 'redux/hooks/KeyMetrics'
import { useMetric } from 'redux/hooks/Metrics'
import { useResourcesForKeyMetric } from 'redux/hooks/ResourceKeyMetricLinker'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'

import StatusBox from 'components_new/molecules/StatusBox'
import TreeDndTable from 'components_new/molecules/TreeDndTable'

import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'
import GroupsDialog from 'components_new/organisms/dialogs/GroupsDialog'
import MetricEditDialog from 'components_new/organisms/dialogs/MetricEditDialog'
import MetricInfoDialog from 'components_new/organisms/dialogs/MetricInfoDialog'
import MetricsDialog from 'components_new/organisms/dialogs/MetricsDialog'
import MetricViewDialog from 'components_new/organisms/dialogs/MetricViewDialog'
import ResourcesDialog from 'components_new/organisms/dialogs/ResourcesDialog'
import MetricPreview from 'components_new/organisms/MetricPreview'

import KeyMetricCells from './KeyMetricCells'

import { formatOrderItem, toTreeNodes, useColumns } from './utils'
import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { getMetricTitle } from 'utils/metric'

// Headless component that triggers data fetching for a single key metric.
// Rendered for every key metric in the tree (including collapsed children) so
// that data is ready before the user expands a parent row.
const KeyMetricDataPrefetch = ({ id }: { id: string }) => {
  const { tryGetKeyMetricData } = useKeyMetricActions()
  const keyMetric = useKeyMetric(id)
  const metric = useMetric(
    keyMetric?.metric.id ?? null,
    keyMetric?.metric.settings.variant_kpi_template_id
  )
  const [rawData, rawDataError] = useKeyMetricRawData(id)
  const isFetching = useKeyMetricRawDataFetching(id)

  useEffect(() => {
    if (isFetching) return

    const noData = !rawData && rawDataError === null
    // Stale data present but variant no longer exists – re-fetch to get 404
    const staleData = !metric && !!rawData && rawDataError === null

    if (noData || staleData) {
      tryGetKeyMetricData(id)
    }
  }, [id, isFetching, rawData, rawDataError, metric])

  return null
}

const getAllKeyMetricIds = (kms: TKeyMetric[]): string[] =>
  kms.flatMap((km) => [km.id, ...getAllKeyMetricIds(km.children)])

export const KeyMetricsTreeContext = createContext<{
  groupId?: string
  onAdd: (keyMetric: TKeyMetric) => void
  onDelete: (keyMetric: TKeyMetric) => void
  onEdit: (keyMetric: TKeyMetric) => void
  onGroups: (keyMetric: TKeyMetric) => void
  onInfo: (keyMetric: TKeyMetric) => void
  onOwner: (keyMetric: TKeyMetric, value: null | string) => void
  onPin: (keyMetric: TKeyMetric) => void
  onPreview: (keyMetric: TKeyMetric) => void
  onResources: (keyMetric: TKeyMetric) => void
    }>({
      groupId: undefined,
      onAdd: () => {},
      onDelete: () => {},
      onEdit: () => {},
      onGroups: () => {},
      onInfo: () => {},
      onOwner: () => {},
      onPin: () => {},
      onPreview: () => {},
      onResources: () => {}
    })

interface KeyMetricsTreeProps {
  editMode: boolean
  groupId?: string
  keyMetrics: TKeyMetric[]
  preview?: boolean
  sx?: SxProps
}

const KeyMetricsTree = (props: KeyMetricsTreeProps) => {
  const { editMode, groupId, keyMetrics, preview, sx } = props

  // actions
  const {
    tryCreateKeyMetric,
    tryDeleteKeyMetric,
    tryPinKeyMetric,
    tryPinKeyMetricOnGroup,
    tryUnpinKeyMetric,
    tryUnpinKeyMetricOnGroup,
    tryUpdateGroupPinComment,
    tryUpdateKeyMetric,
    tryUpdatePinComment,
    tryUpdateOrderKeyMetric
  } = useKeyMetricActions()
  const { t, lang, translateEnum } = useTranslation()
  const group = useGroup(groupId ?? '')

  const treeData = toTreeNodes(keyMetrics)

  const allKeyMetricIds = useMemo(
    () => getAllKeyMetricIds(keyMetrics),
    [keyMetrics]
  )

  const LOCAL_STORAGE_KEY = 'key-metrics-expanded'

  const [expandedIds, setExpandedIds] = useState<string[]>(() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY)

    return stored ? JSON.parse(stored) : []
  })

  const handleExpandChange = useCallback((ids: string[]) => {
    setExpandedIds(ids)
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(ids))
  }, [])

  const columns = useColumns({ editMode, preview })

  // dialogs
  const [dialog, setDialog] = useState<{
    keyMetric: TKeyMetric | null
    variant:
      | 'add'
      | 'delete'
      | 'edit'
      | 'groups'
      | 'info'
      | 'preview'
      | 'resources'
  } | null>(null)

  const dialogKeyMetricId = dialog?.keyMetric?.id ?? ''
  const dialogGroupIds = useGroupIdsForKeyMetric(dialogKeyMetricId)
  const dialogResources = useResourcesForKeyMetric(dialogKeyMetricId)
  const { allGroups } = useGroups()

  const dialogGroups = useMemo(
    () => allGroups.filter((g) => dialogGroupIds.includes(g.id)),
    [allGroups, dialogGroupIds]
  )

  // add
  const handleAdd = useCallback((keyMetric: TKeyMetric | null) => {
    setDialog({
      keyMetric: keyMetric,
      variant: 'add'
    })
  }, [])

  // delete
  const handleDelete = useCallback((keyMetric: TKeyMetric) => {
    setDialog({
      keyMetric: keyMetric,
      variant: 'delete'
    })
  }, [])

  // edit
  const handleEdit = useCallback((keyMetric: TKeyMetric) => {
    setDialog({
      keyMetric: keyMetric,
      variant: 'edit'
    })
  }, [])

  // groups
  const handleGroups = useCallback((keyMetric: TKeyMetric) => {
    setDialog({
      keyMetric: keyMetric,
      variant: 'groups'
    })
  }, [])

  // info
  const handleInfo = useCallback((keyMetric: TKeyMetric) => {
    setDialog({
      keyMetric: keyMetric,
      variant: 'info'
    })
  }, [])

  // pin
  const [justPinnedId, setJustPinnedId] = useState<string | null>(null)

  useEffect(() => {
    setJustPinnedId(null)
  }, [editMode])

  const handlePin = useCallback(
    (keyMetric: TKeyMetric) => {
      if (group) {
        const pin = group?.pins.find((p) => p.key_metric_id === keyMetric.id)

        if (pin) {
          tryUnpinKeyMetricOnGroup(pin.id, group.id)
          setJustPinnedId(null)
        } else {
          tryPinKeyMetricOnGroup(keyMetric.id, group.id, '')
          setJustPinnedId(keyMetric.id)
        }

        return
      }

      if (keyMetric.pinned && keyMetric.pin_id) {
        tryUnpinKeyMetric(keyMetric.pin_id)
        setJustPinnedId(null)
      } else {
        tryPinKeyMetric(keyMetric.id, '')
        setJustPinnedId(keyMetric.id)
      }
    },
    [
      group,
      tryPinKeyMetric,
      tryPinKeyMetricOnGroup,
      tryUnpinKeyMetric,
      tryUnpinKeyMetricOnGroup
    ]
  )

  // preview
  const handlePreview = useCallback((keyMetric: TKeyMetric) => {
    setDialog({
      keyMetric: keyMetric,
      variant: 'preview'
    })
  }, [])

  // resources
  const handleResources = useCallback((keyMetric: TKeyMetric) => {
    setDialog({
      keyMetric: keyMetric,
      variant: 'resources'
    })
  }, [])

  // owner
  const handleOwner = useCallback(
    (keyMetric: TKeyMetric, value: string | null) => {
      tryUpdateKeyMetric(keyMetric.id, {
        responsible_account_id: value
      })
    },
    []
  )

  // close
  const handleClose = () => {
    setDialog(null)
  }

  // pinned
  const pinnedKeyMetrics = useMemo(() => {
    const kms: TKeyMetric[] = []

    const traverse = (km: TKeyMetric) => {
      if (km.pinned) {
        kms.push(km)
      }

      km.children.forEach(traverse)
    }

    keyMetrics.forEach(traverse)

    return kms.sort((a, b) => sortAlphabeticalAsc(a.title, b.title))
  }, [keyMetrics])

  return (
    <KeyMetricsTreeContext.Provider
      value={{
        groupId,
        onAdd: handleAdd,
        onDelete: handleDelete,
        onEdit: handleEdit,
        onGroups: handleGroups,
        onInfo: handleInfo,
        onOwner: handleOwner,
        onPin: handlePin,
        onPreview: handlePreview,
        onResources: handleResources
      }}
    >
      <Box sx={sx}>
        {pinnedKeyMetrics.length > 0 ? (
          <Grid2 container={true} spacing={2} sx={{ mb: 4 }}>
            {pinnedKeyMetrics.map((keyMetric) => (
              <Grid2 key={keyMetric.id} sm={6} xs={12}>
                <MetricPreview
                  autoFocusPinComment={keyMetric.id === justPinnedId}
                  editMode={editMode}
                  id={keyMetric.metric.id}
                  keyMetric={keyMetric}
                  onChangePinComment={(value) => {
                    if (!keyMetric.pin_id) return

                    if (groupId) {
                      tryUpdateGroupPinComment(keyMetric.pin_id, groupId, value)
                    } else {
                      tryUpdatePinComment(keyMetric.pin_id, value)
                    }
                  }}
                  onGroups={() => handleGroups(keyMetric)}
                  onResources={() => handleResources(keyMetric)}
                  onView={() => handlePreview(keyMetric)}
                  options={[
                    {
                      iconName: keyMetric.pinned ? 'Unpin' : ('Pin' as const),
                      onClick: () => handlePin(keyMetric),
                      title: t('global', keyMetric.pinned ? 'unpin' : 'pin')
                    }
                  ]}
                  settings={keyMetric.metric.settings}
                />
              </Grid2>
            ))}
          </Grid2>
        ) : null}

        <TreeDndTable
          CellsComponent={KeyMetricCells}
          columns={columns}
          data={treeData}
          disableDrag={Boolean(groupId)}
          editMode={editMode}
          expandedIds={expandedIds}
          onExpandChange={handleExpandChange}
          onRowAdd={!groupId ? () => handleAdd(null) : undefined}
          onTreeChange={(value) => {
            tryUpdateOrderKeyMetric(
              value.map((v, index) => formatOrderItem(v, index))
            )
          }}
        />
      </Box>

      {/*-- dialogs --*/}

      {/*-- add --*/}
      <MetricsDialog
        availableSteps={['slices', 'distribution', 'rollingSum']}
        creating={false}
        handleOnAdd={(metric, settings) => {
          const title = getMetricTitle(metric, settings, lang, translateEnum)

          tryCreateKeyMetric(
            {
              kpi_template_id: metric.id,
              parent_id: dialog?.keyMetric ? dialog.keyMetric.id : null,
              title,
              ...settings
            },
            (err) => {
              if (!err) handleClose()
            }
          )
        }}
        onClose={handleClose}
        open={dialog?.variant === 'add'}
        title={t('key_metrics', 'add_title')}
        variant="metrics"
      />

      {dialog?.keyMetric ? (
        <>
          {/*-- delete --*/}
          <DeleteDialog
            onClose={handleClose}
            onDelete={(cb) => {
              if (!dialog.keyMetric) return

              tryDeleteKeyMetric(
                dialog.keyMetric.id,
                dialog.keyMetric.parent?.id ?? null,
                (err) => {
                  if (!err) handleClose()
                  cb()
                }
              )
            }}
            open={dialog.variant === 'delete'}
            title={dialog.keyMetric.title}
            type={t('key_metrics', 'key_metric', true)}
            unlockPhrase={t('global', 'delete')}
          >
            {dialog?.keyMetric.children.length > 0 ? (
              <StatusBox severity="warning" title={t('global', 'note')}>
                {t('key_metrics', 'delete_key_metric_note')}
              </StatusBox>
            ) : null}
          </DeleteDialog>

          {/*-- edit --*/}
          <MetricEditDialog
            keyMetric={dialog.keyMetric}
            metric={dialog.keyMetric.metric}
            onClose={handleClose}
            onSave={(metric, keyMetric) => {
              if (!dialog.keyMetric || !keyMetric) return

              tryUpdateKeyMetric(dialog.keyMetric.id, {
                group_ids: keyMetric.groups.map((g) => g.id),
                kpi_template_id: metric.id,
                resources: keyMetric.resources.map((r) => ({
                  id: r.id,
                  type: getResourceVariant(r)
                })),
                responsible_account_id: keyMetric.responsible_account_id,
                ...metric.settings,
                target_activated: keyMetric.target_activated,
                target_value: keyMetric.target_value,
                title: keyMetric.title,
                increase_is_positive: keyMetric.increase_is_positive
              })
            }}
            open={dialog.variant === 'edit'}
          />

          {/*-- groups --*/}
          <GroupsDialog
            groups={dialogGroups}
            onAdd={(groupId: string) => {
              if (!dialog.keyMetric) return

              tryUpdateKeyMetric(dialog.keyMetric.id, {
                group_ids: [...dialogGroupIds, groupId]
              })
            }}
            onClose={handleClose}
            onRemove={(groupId) => {
              if (!dialog.keyMetric) return

              tryUpdateKeyMetric(dialog.keyMetric.id, {
                group_ids: dialogGroupIds.filter((gid) => gid !== groupId)
              })
            }}
            open={dialog.variant === 'groups'}
          />

          {/*-- info --*/}
          <MetricInfoDialog
            metric={dialog.keyMetric.metric}
            onClose={handleClose}
            open={dialog.variant === 'info'}
            title={dialog.keyMetric.title}
          />

          {/*-- preview --*/}
          <MetricViewDialog
            keyMetric={dialog.keyMetric}
            metric={dialog.keyMetric.metric}
            onClose={handleClose}
            open={dialog.variant === 'preview'}
          />

          {/*-- resources --*/}
          <ResourcesDialog
            onAdd={(id, variant) => {
              if (!dialog.keyMetric || variant === 'folder') return

              const currentResources = dialogResources.map((r) => ({
                id: r.id,
                type: getResourceVariant(r)
              }))

              tryUpdateKeyMetric(dialog.keyMetric.id, {
                resources: [...currentResources, { id, type: variant }]
              })
            }}
            onClose={handleClose}
            onRemove={(id) => {
              if (!dialog.keyMetric) return

              tryUpdateKeyMetric(dialog.keyMetric.id, {
                resources: dialogResources
                  .filter((r) => r.id !== id)
                  .map((r) => ({
                    id: r.id,
                    type: getResourceVariant(r)
                  }))
              })
            }}
            open={dialog.variant === 'resources'}
            resources={dialogResources.map((r) => ({
              href: isDashboardResource(r)
                ? `/dashboards/${r.dashboards[0].id}`
                : `/reports/${r.id}`,
              id: r.id,
              title: r.title,
              variant: getResourceVariant(r)
            }))}
          />
        </>
      ) : null}

      {allKeyMetricIds.map((id) => (
        <KeyMetricDataPrefetch key={id} id={id} />
      ))}
    </KeyMetricsTreeContext.Provider>
  )
}

export default KeyMetricsTree
